/**
 * Modal HEIGHT tokens — extracted from `overlayStyles.js` (2026-09-27, touch-band
 * work) to keep both files under the 500-line cap. Re-exported from
 * `overlayStyles.js` unchanged, so every existing `import { MODAL_COMPACT_MAX_HEIGHT }
 * from '.../overlayStyles'` keeps working without a call-site edit.
 */

/**
 * CSS custom properties published on `document.documentElement` by
 * `useModalTouchReserves` (src/hooks/ui/useModalTouchReserves.js) while a
 * phone/tablet role shell (`MobileNavShell` — every role funnels through it,
 * directly for admin, via `MobileRoleNavFrame` for billing/worker/supervisor/
 * platform) is mounted. Unset (falls back to `0px`) on desktop and on any
 * screen that never mounts that shell (login, splash) — so every `min()` cap
 * below that folds in `MODAL_TOUCH_BAND_MAX_HEIGHT` is byte-identical there.
 *
 * `--cvx-modal-top-reserve`    = the rendered shell header's bottom edge (px).
 * `--cvx-modal-bottom-reserve` = viewport bottom minus the bottom nav pill/
 *                                dock's top edge (px) — the tablet dock counts
 *                                as the pill.
 */
export const MODAL_TOP_RESERVE_VAR = '--cvx-modal-top-reserve';
export const MODAL_BOTTOM_RESERVE_VAR = '--cvx-modal-bottom-reserve';

/**
 * The one height rule every modal family measures against: the visible viewport
 * MINUS the parts the hardware occupies. `100dvh` is the *visible* viewport but
 * it still spans the notch / Dynamic Island and the home indicator — so a card
 * sized off raw dvh renders correctly and is still partly under the hardware.
 *
 * Confirmed on an iPhone (2026-07-31): the bulk-delete confirm at full height
 * put its title under the Dynamic Island. Nothing in the chain subtracted an
 * inset — not this panel, not the scrim.
 *
 * The 32px is breathing room so a full-height card clears the inset visibly
 * rather than sitting flush against it. Kept in ONE place so the compact and
 * tall families can never drift apart again.
 */
export const MODAL_SAFE_MAX_HEIGHT =
  'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 32px)';

/**
 * Founder standing order (2026-09-27): a centered modal never renders past the
 * bottom of the header at the top or the top of the bottom nav pill/dock —
 * every shared cap below folds this in via `min()`. `MODAL_TOP_RESERVE_VAR` /
 * `MODAL_BOTTOM_RESERVE_VAR` are 0 whenever the touch shell isn't publishing
 * them (desktop, pre-shell screens), so this term is then simply `100dvh -
 * 24px` — never SMALLER than the existing `MODAL_SAFE_MAX_HEIGHT` / `90dvh` /
 * `78vh` terms it's min()'d against there, meaning it can never tighten a cap
 * on a surface that doesn't publish the reserves. The 24px is 12px of
 * breathing room above AND below the band, matching the scrim's own vertical
 * rhythm rather than inventing a new one.
 */
export const MODAL_TOUCH_BAND_MAX_HEIGHT =
  `calc(100dvh - var(${MODAL_TOP_RESERVE_VAR}, 0px) - var(${MODAL_BOTTOM_RESERVE_VAR}, 0px) - 24px)`;

/**
 * THE height rule for the compact modal family — confirms, small forms, pickers.
 * Import this instead of hand-writing a cap; it is the one place the terms
 * are balanced:
 *
 *   90dvh                       proportion — never fill the whole screen
 *   MODAL_SAFE_MAX_HEIGHT       hardware   — clear the notch and home indicator
 *   720px                       desktop    — a confirm is a dialog, not a page
 *   MODAL_TOUCH_BAND_MAX_HEIGHT touch      — never past the header/pill band
 *
 * The 720px term exists because `90dvh` on a 1440px monitor is ~1296px: a
 * confirm dialog taller than most laptop screens. 720 is not invented — five
 * modals had independently arrived at `min(90dvh, 720px)`, so this standardises
 * on intent that was already in the codebase.
 *
 * TALL content — PDF previews, the invoice/estimate builders — is a DIFFERENT
 * family and must NOT use this; see `mobileFullscreenModalSizing` (78vh), which
 * is deliberately matched to the builder wizards.
 *
 * min() throughout, so adopting it can only ever SHRINK a modal, never grow one.
 */
export const MODAL_COMPACT_MAX_HEIGHT =
  `min(90dvh, ${MODAL_SAFE_MAX_HEIGHT}, 720px, ${MODAL_TOUCH_BAND_MAX_HEIGHT})`;

/**
 * Same rule WITHOUT the 720px desktop ceiling, for large modals — builders,
 * wizards, pickers, bulk progress panels.
 *
 * Why the ceiling is withheld rather than applied everywhere: a cap only helps
 * if the content can SCROLL inside it. Several of these modals have no internal
 * scroll region of their own and rely on the shell — and AnimatedModalShell
 * (unlike ConfirmDialog, which owns `dialogBodyScrollStyle`) supplies none. On
 * those, tightening the cap from ~1296px to 720px would CLIP content that
 * renders fine today, which is the exact failure mode the modal-height audit
 * found in five other modals.
 *
 * This token still delivers the two unconditional wins: safe-area clearance,
 * and dvh instead of vh (the copies it replaces used `vh`, which disagrees with
 * dvh in mobile Safari when the URL bar shows — invisible in the installed PWA,
 * which is exactly why it drifted).
 *
 * TO GIVE ONE OF THESE THE DESKTOP CEILING: verify it has a real internal scroll
 * region, then switch it to MODAL_COMPACT_MAX_HEIGHT. Per-modal, not en masse.
 */
export const MODAL_LARGE_MAX_HEIGHT =
  `min(90dvh, ${MODAL_SAFE_MAX_HEIGHT}, ${MODAL_TOUCH_BAND_MAX_HEIGHT})`;

/**
 * Mobile cap for the large/picker family — a centered card, not a sheet.
 * Same rule as the builder wizards (`builderWizardPanelStyleMobile`), now with
 * the touch-band term folded in:
 *
 *   78vh                         proportion — gap above and below on a tall phone
 *   100dvh − insets − 64px       hardware — clear notch, home indicator, scrim gutter
 *   MODAL_TOUCH_BAND_MAX_HEIGHT  touch — never past the header/pill band
 *
 * The 78vh term is a DESIGN proportion (do not fill the screen). The dvh term
 * is the safe-area floor for Safari browser mode, where the visible viewport
 * is shorter than 78vh. min() so this can only shrink the card.
 */
export const MODAL_LARGE_MAX_HEIGHT_MOBILE =
  `min(78vh, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 64px), ${MODAL_TOUCH_BAND_MAX_HEIGHT})`;

/**
 * Mobile sizing for preview-style modals (Invoice / Estimate / Report). Spread
 * into each preview's `modalMobile` so the safe-area math lives in one place.
 *
 * CENTERED CARD (no top-pin margins). Paired with the centered preview scrim
 * (`overlayMobile` no longer anchors `flex-start`), the modal reads as a
 * vertically-centered card with a clear gap top + bottom — matching the manual
 * invoice/estimate builder wizards (the "previews look full-screen, not
 * centered like the builders" report). `78vh` is the SAME cap as
 * `builderWizardPanelStyleMobile`, so previews and builders are the identical
 * size on tall phones; the `dvh` term is the safe-area floor for the shorter
 * Safari-browser viewport (− 24 covers the scrim's 8px padding + breathing
 * room). The touch-band term is folded in last, so a preview never renders
 * past the header/pill band either. A DEFINITE `height` (not just maxHeight)
 * so the PDF canvas (`flex: 1`) fills the card.
 */
export const mobileFullscreenModalSizing = {
  height:
    `min(78vh, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px), ${MODAL_TOUCH_BAND_MAX_HEIGHT})`,
  maxHeight:
    `min(78vh, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px), ${MODAL_TOUCH_BAND_MAX_HEIGHT})`,
};
